import type { AvaturnHead } from "@avaturn-live/web-sdk";

export class AvatarSessionError extends Error {}

type WaitForAvatarSessionOptions = {
  timeoutMs?: number;
  pollIntervalMs?: number;
  /** Aborts the wait early, e.g. when the caller's own init error flag flips. */
  isAborted?: () => boolean;
};

/**
 * Waits until the SDK can accept `join()`.
 *
 * `avatar.init()` cannot be awaited before joining: the promise it returns
 * settles on the SDK's "init" event, which fires only once the avatar's first
 * video track arrives — and that happens after `join()`. What `join()` actually
 * needs is the session `init()` fetches earlier; `session_id` is assigned in the
 * same statement as that session, so it doubles as the readiness signal.
 */
export async function waitForAvatarSession(
  avatar: AvaturnHead,
  {
    timeoutMs = 15_000,
    pollIntervalMs = 50,
    isAborted,
  }: WaitForAvatarSessionOptions = {},
): Promise<void> {
  const startedAt = Date.now();

  while (!avatar.session_id) {
    if (isAborted?.()) {
      throw new AvatarSessionError("Avatar initialization failed.");
    }
    if (Date.now() - startedAt >= timeoutMs) {
      throw new AvatarSessionError("Avatar session was not ready in time.");
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }
}
